import { useEffect, useRef, useState } from "react";
import FrameIssueBalloons, { type BalloonDock } from "@/components/audits/replay/FrameIssueBalloons";
import FrameIssueCaption, { type FrameIssueEmptyCaption } from "@/components/audits/replay/FrameIssueCaption";
import { objectContainRectPosition } from "@/components/audits/replay/helpers";
import IssueDetailModal from "@/components/issues/IssueDetailModal";
import type { Finding } from "@/types";

type Props = {
  findings: Finding[];
  frameId: string;
  frameSize: { width: number; height: number };
  imageAspect: number;
  highlightFindingId?: string | null;
  emptyCaption?: FrameIssueEmptyCaption;
  compactDetail?: boolean;
  staticCallout?: boolean;
  dock?: BalloonDock;
  balloonDockById?: Record<string, BalloonDock>;
  onSelectFinding?: (findingId: string) => void;
  onOpenFinding?: (findingId: string) => void;
};

type NumberedIssue = {
  finding: Finding;
  n: number;
  group: "high" | "medium" | "low";
  sevLabel: string;
};

export default function FrameIssueOverlay({
  findings,
  frameId,
  frameSize,
  imageAspect,
  highlightFindingId = null,
  emptyCaption,
  compactDetail = false,
  staticCallout = false,
  dock = "auto",
  balloonDockById,
  onSelectFinding,
  onOpenFinding,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [openFindingId, setOpenFindingId] = useState<string | null>(null);
  const [originEl, setOriginEl] = useState<HTMLElement | null>(null);
  const balloonNodes = useRef<Record<string, HTMLElement | null>>({});
  const highlightNeedsCaption = Boolean(
    highlightFindingId && findings.some((finding) => finding.id === highlightFindingId && !finding.bounding_box),
  );

  useEffect(() => {
    setHoveredId(null);
    setOpenFindingId(null);
    setOriginEl(null);
  }, [frameId]);

  useEffect(() => {
    if (highlightNeedsCaption) {
      setExpanded(true);
    }
  }, [frameId, highlightNeedsCaption]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      setHoveredId(null);
      setExpanded(false);
      setOpenFindingId(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const issues = numberedIssues(findings);
  const showNumber = issues.length > 1;
  const located = issues.flatMap((issue) => {
    const box = issue.finding.bounding_box;
    if (!box) {
      return [];
    }

    return [
      {
        id: issue.finding.id,
        n: issue.n,
        title: issue.finding.title || "Untitled issue",
        description: issue.finding.description,
        group: issue.group,
        sevLabel: issue.sevLabel,
        rect: objectContainRectPosition({
          containerWidth: frameSize.width,
          containerHeight: frameSize.height,
          imageAspect,
          xMin: box.x_min / 10,
          yMin: box.y_min / 10,
          xMax: box.x_max / 10,
          yMax: box.y_max / 10,
        }),
        dock: balloonDockById?.[issue.finding.id],
      },
    ];
  });
  const captionIssues = issues.map((issue) => ({
    id: issue.finding.id,
    n: issue.n,
    title: issue.finding.title || "Untitled issue",
    group: issue.group,
    sevLabel: issue.sevLabel,
  }));
  const openIssue = issues.find((issue) => issue.finding.id === openFindingId) ?? null;
  const highlightId = issues.some((issue) => issue.finding.id === highlightFindingId) ? highlightFindingId : null;
  const activeHoverId = hoveredId ?? highlightId;

  const openFinding = (id: string, origin: HTMLElement | null) => {
    onOpenFinding?.(id);
    if (openFindingId === id) {
      setOpenFindingId(null);
      return;
    }

    setOriginEl(balloonNodes.current[id] ?? origin);
    setOpenFindingId(id);
  };

  const registerBalloon = (id: string, node: HTMLElement | null) => {
    balloonNodes.current[id] = node;
  };

  return (
    <>
      {compactDetail && openFindingId && !staticCallout ? (
        <button
          type="button"
          className="absolute inset-0 z-15 cursor-default"
          aria-label="Close finding"
          onClick={() => setOpenFindingId(null)}
        />
      ) : null}
      <FrameIssueBalloons
        issues={located}
        showNumber={showNumber}
        hoveredId={activeHoverId}
        openId={openFindingId}
        frameSize={frameSize}
        staticCallout={staticCallout}
        expandInline={compactDetail}
        dock={dock}
        interactive={Boolean(onSelectFinding) || !staticCallout}
        onHover={setHoveredId}
        onOpen={onSelectFinding ?? (staticCallout ? () => undefined : openFinding)}
        onBalloonNode={registerBalloon}
      />
      {compactDetail ? null : (
        <FrameIssueCaption
          issues={captionIssues}
          showNumber={showNumber}
          expanded={expanded}
          hoveredId={activeHoverId}
          openId={openFindingId}
          emptyCaption={emptyCaption}
          onToggle={() => setExpanded((value) => !value)}
          onHover={setHoveredId}
          onOpen={openFinding}
        />
      )}
      {openIssue && !compactDetail ? (
        <IssueDetailModal
          issue={openIssue.finding}
          n={showNumber ? openIssue.n : undefined}
          group={openIssue.group}
          originEl={originEl}
          onClose={() => setOpenFindingId(null)}
        />
      ) : null}
    </>
  );
}

function numberedIssues(findings: Finding[]): NumberedIssue[] {
  return [...findings]
    .sort((first, second) => (second.severity || 0) - (first.severity || 0))
    .map((finding, index) => {
      const group = issueGroup(finding.priority);
      return {
        finding,
        n: index + 1,
        group,
        sevLabel: severityLabel(group),
      };
    });
}

function issueGroup(priority: Finding["priority"]): NumberedIssue["group"] {
  if (priority === "critical" || priority === "high") return "high";
  if (priority === "medium") return "medium";

  return "low";
}

function severityLabel(group: NumberedIssue["group"]) {
  if (group === "high") return "High Severity";
  if (group === "medium") return "Medium Severity";

  return "Low Severity";
}
